import type { LessonDataSourceEnum, SlideCountInput } from "@/generated/graphql";
import { getTemplateLabel } from "@/lib/slides/slideTemplates";
import {
  type ContextLines,
  LESSON_DATA_SOURCES,
  type LessonDataSource,
  type LessonDataSourceCounts,
  type SlideCount,
  countsWithEdits,
  resolveSlideCount as resolveCount,
  tidyLines,
} from "@ai-teach/utils";
import type { BookLessonRef, DataSource, GeneratorSection, SubslideParent } from "./types";

interface DataSourceInfo {
  label: string;
  item: string;
}

export const DATA_SOURCES: Record<LessonDataSource, DataSourceInfo> = {
  words: { label: "Words", item: "word" },
  grammar: { label: "Grammar points", item: "grammar point" },
  grammarExamples: { label: "Grammar examples", item: "grammar example" },
  passages: { label: "Reading passages", item: "passage" },
  dialogues: { label: "Dialogues", item: "dialogue" },
};

export const DATA_SOURCE_ORDER = LESSON_DATA_SOURCES;

/** A count has to name a source the section reads, or the generator would write no slides for it. */
export function isCountableSource(countFrom: string, subLists: string[]): boolean {
  return (
    (LESSON_DATA_SOURCES as readonly string[]).includes(countFrom) || subLists.includes(countFrom)
  );
}

/** What one item is called: a data source has its own word, a sub-list is named by the book. */
export function countedItem(countFrom: string): string {
  const source = DATA_SOURCES[countFrom as LessonDataSource];
  return source ? source.item : `${countFrom} word`;
}

/** Each sub-list is counted under its own name, the way a section counts per one of them. */
export function dataSourceCounts(book: BookLessonRef): LessonDataSourceCounts {
  return {
    ...book.subListCounts,
    words: book.wordCount,
    grammar: book.grammarCount,
    grammarExamples: book.grammarExampleCount,
    passages: book.passageCount,
    dialogues: book.dialogueCount,
  };
}

/**
 * Templates whose count the generator reads as a ceiling, not a target: the model is handed the
 * lesson's material and writes however many slides it actually teaches, up to this many.
 */
const CAPPED_TEMPLATES: readonly string[] = ["spellingToSound"];

export function countsAsCap(template: string | null): boolean {
  return template !== null && CAPPED_TEMPLATES.includes(template);
}

export function describeCount(count: SlideCount): string {
  if (count.rule === "fixed") return `${count.total} slide${count.total === 1 ? "" : "s"}`;
  const item = countedItem(count.countFrom);
  return count.each === 1 ? `One per ${item}` : `${count.each} per ${item}`;
}

export function describeParent(parent: SubslideParent, tagName: string): string {
  const slides = parent.each === 1 ? "subslide" : "subslides";
  return `${parent.each} ${slides} per ${getTemplateLabel(parent.template)} slide tagged "${tagName}"`;
}

/** What a section builds, in one line: how many slides, then what they are written from. */
export function describeSection(
  section: Pick<GeneratorSection, "context" | "sectionScopes" | "count" | "parent">,
  parentTagName: string
): string {
  if (section.parent) return describeParent(section.parent, parentTagName);
  const sources = section.context.map((one) => DATA_SOURCES[one].label).join(", ");
  const scopes = section.sectionScopes?.length ? ` in ${section.sectionScopes.join(", ")}` : "";
  const read = `${sources || (scopes ? "" : "no context")}${scopes}`;
  return section.count ? `${describeCount(section.count)} · ${read}` : read;
}

/** The author's rows tidied the way the server tidies them, blank ones between passages kept. */
export function writtenLines(edits: ContextLines[]): ContextLines[] {
  return edits.map((edit) => ({ ...edit, lines: tidyLines(edit.lines) }));
}

export function resolveSlideCount(
  count: SlideCount,
  book: BookLessonRef | null,
  edits: ContextLines[] = []
): number | null {
  if (count.rule === "fixed") return count.total;
  if (!book) return null;
  return resolveCount(count, countsWithEdits(dataSourceCounts(book), writtenLines(edits)));
}

/** The count as GraphQL carries it: one flat shape for both rules, every field optional. */
interface StoredSlideCount {
  rule: string;
  countFrom?: string | null;
  each?: number | null;
  total?: number | null;
}

export function toSlideCount(count: StoredSlideCount): SlideCount {
  if (count.rule === "fixed") return { rule: "fixed", total: count.total ?? 1 };
  return {
    rule: "perSourceItem",
    countFrom: count.countFrom ?? "words",
    each: count.each ?? 1,
  };
}

export function toCountInput(count: SlideCount): SlideCountInput {
  if (count.rule === "fixed") return { rule: "fixed", total: count.total };
  return {
    rule: "perSourceItem",
    countFrom: count.countFrom,
    each: count.each,
  };
}
